import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, ChevronDown, ChevronUp } from 'lucide-react';
import textToSpeechService from '../services/textToSpeech';

const SoundButton = ({ 
  text, 
  title = "播放", 
  className = "",
  showLanguageSelector = true,
  componentId = null,
  onKaraokeUpdate = null // Callback for karaoke highlighting
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('mandarin');
  const [currentCharIndex, setCurrentCharIndex] = useState(-1);
  const [karaokeText, setKaraokeText] = useState('');
  const intervalRef = useRef(null);
  const charTimingRef = useRef([]);

  useEffect(() => {
    // Check if speech is playing when component mounts or text changes
    const checkPlayingStatus = () => {
      const isCurrentlyPlaying = textToSpeechService.isComponentPlaying(componentId);
      setIsPlaying(isCurrentlyPlaying);
      
      if (!isCurrentlyPlaying) {
        // Reset karaoke when stopped
        setCurrentCharIndex(-1);
        setKaraokeText('');
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    };

    // Set up interval to check playing status
    const interval = setInterval(checkPlayingStatus, 100);

    return () => {
      clearInterval(interval);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [text, componentId]);

  // Calculate character timing for karaoke effect
  const calculateCharTiming = (text) => {
    if (!text) return [];
    
    const chars = text.split('');
    const totalDuration = text.length * 150; // Average 150ms per character
    const timings = [];
    
    chars.forEach((char, index) => {
      const startTime = (index / chars.length) * totalDuration;
      const endTime = ((index + 1) / chars.length) * totalDuration;
      timings.push({
        char,
        startTime,
        endTime,
        index
      });
    });
    
    return timings;
  };

  // Start karaoke effect
  const startKaraoke = (text) => {
    const timings = calculateCharTiming(text);
    charTimingRef.current = timings;
    
    const startTime = Date.now();
    setKaraokeText(text);
    
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const currentChar = timings.find(timing => 
        elapsed >= timing.startTime && elapsed < timing.endTime
      );
      
      if (currentChar) {
        setCurrentCharIndex(currentChar.index);
        if (onKaraokeUpdate) {
          onKaraokeUpdate(currentChar.index, text);
        }
      } else if (elapsed >= timings[timings.length - 1]?.endTime) {
        // End of text
        setCurrentCharIndex(-1);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    }, 50); // Update every 50ms for smooth highlighting
  };

  const handlePlay = async (language = selectedLanguage) => {
    if (!text || !text.trim()) {
      console.warn('No text to play');
      return;
    }

    try {
      setIsPlaying(true);
      startKaraoke(text);
      await textToSpeechService.speakText(text, language, componentId);
    } catch (error) {
      console.error('Error playing text:', error);
      setIsPlaying(false);
      setCurrentCharIndex(-1);
      setKaraokeText('');
    }
  };

  const handleStop = () => {
    textToSpeechService.stop();
    setIsPlaying(false);
    setCurrentCharIndex(-1);
    setKaraokeText('');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language);
    setShowLanguageMenu(false);
    if (isPlaying) {
      handleStop();
    }
    handlePlay(language);
  };

  const languages = [
    { key: 'mandarin', label: '普通話', flag: '🇨🇳' },
    { key: 'cantonese', label: '粵語', flag: '🇭🇰' }
  ];

  // Render karaoke text with highlighting
  const renderKaraokeText = () => {
    if (!karaokeText) return null;
    
    return (
      <div className="karaoke-text mt-2 p-3 bg-blue-50 rounded-lg border">
        <div className="text-sm text-gray-600 mb-1">跟讀模式 (Karaoke Mode)</div>
        <div className="text-base leading-relaxed">
          {karaokeText.split('').map((char, index) => (
            <span
              key={index}
              className={`inline-block transition-all duration-100 ${
                index === currentCharIndex
                  ? 'bg-yellow-300 text-black font-bold scale-110'
                  : index < currentCharIndex
                  ? 'text-gray-500'
                  : 'text-gray-700'
              }`}
              style={{
                transform: index === currentCharIndex ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.1s ease-in-out'
              }}
            >
              {char}
            </span>
          ))}
        </div>
        {isPlaying && (
          <div className="mt-2 text-xs text-blue-600">
            🔊 正在播放中... (Playing...)
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative inline-block ${className}`}>
      <div className="flex items-center gap-2">
        <button
          onClick={isPlaying ? handleStop : () => handlePlay()}
          className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-all duration-200 ${
            isPlaying 
              ? 'bg-red-500 text-white hover:bg-red-600' 
              : 'bg-blue-500 text-white hover:bg-blue-600'
          }`}
          title={isPlaying ? '停止播放' : `${title} (${selectedLanguage === 'mandarin' ? '普通話' : '粵語'})`}
        >
          {isPlaying ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          <span className="text-sm">{isPlaying ? '停止' : title}</span>
        </button>

        {showLanguageSelector && (
          <div className="relative">
            <button
              onClick={() => setShowLanguageMenu(!showLanguageMenu)}
              className="flex items-center gap-1 px-2 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              title="選擇語言"
            >
              <span className="text-sm">
                {languages.find(lang => lang.key === selectedLanguage)?.flag || '🇨🇳'}
              </span>
              {showLanguageMenu ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {showLanguageMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[120px]">
                {languages.map((language) => (
                  <button
                    key={language.key}
                    onClick={() => handleLanguageSelect(language.key)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                      selectedLanguage === language.key ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                    }`}
                  >
                    <span>{language.flag}</span>
                    <span>{language.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Karaoke display */}
      {renderKaraokeText()}

      {/* Click outside to close language menu */}
      {showLanguageMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowLanguageMenu(false)}
        />
      )}
    </div>
  );
};

export default SoundButton; 